import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { staggerContainer, netflixCardHover, appStoreCardAnimation } from '../../../utils/animations';
import { ExternalLink, Download, Play, Star, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';

interface CardItem {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  image?: string;
  icon?: React.ReactNode;
  link?: string;
  badge?: string;
  rating?: number;
  accentColor?: string;
  ctaText?: string;
  overlayIntensity?: number;
  textPosition?: 'top' | 'center' | 'bottom';
}

interface PremiumCardsProps {
  title?: string;
  subtitle?: string;
  titleAlign?: string;
  titleSize?: string;
  titleWeight?: string;
  subtitleSize?: string;
  titleGap?: string;
  items: CardItem[];
  variant?: 'netflix' | 'appstore' | 'glass' | 'overlay';
  columns?: number;
  scrollable?: boolean;
  showArrows?: boolean;
  cardWidth?: 'small' | 'medium' | 'large' | 'xl';
  gap?: number;
  styles?: any; // Accept styles from parent
}

// Helper functions for Tailwind classes
const getTextAlignClass = (align?: string): string => {
  switch(align) {
    case 'center': return 'text-center';
    case 'right': return 'text-right';
    case 'left': return 'text-left';
    default: return 'text-left';
  }
};

const getTitleSizeClass = (size?: string): string => {
  switch(size) {
    case 'text-sm': return 'text-sm';
    case 'text-base': return 'text-base';
    case 'text-lg': return 'text-lg';
    case 'text-xl': return 'text-xl';
    case 'text-2xl': return 'text-2xl';
    case 'text-3xl': return 'text-3xl';
    case 'text-4xl': return 'text-4xl';
    case 'text-5xl': return 'text-5xl';
    default: return 'text-base';
  }
};

// Special helper for section titles with responsive sizing
const getSectionTitleClass = (size?: string): string => {
  switch(size) {
    case 'text-xl': return 'text-xl md:text-2xl';
    case 'text-2xl': return 'text-2xl md:text-3xl';
    case 'text-3xl': return 'text-3xl md:text-4xl';
    case 'text-4xl': return 'text-4xl md:text-5xl';
    case 'text-5xl': return 'text-5xl md:text-6xl';
    case 'text-6xl': return 'text-6xl md:text-7xl';
    default: return 'text-3xl md:text-4xl';
  }
};

const getFontWeightClass = (weight?: string): string => {
  switch(weight) {
    case 'font-normal': return 'font-normal';
    case 'font-medium': return 'font-medium';
    case 'font-semibold': return 'font-semibold';
    case 'font-bold': return 'font-bold';
    case 'font-extrabold': return 'font-extrabold';
    default: return 'font-semibold';
  }
};

const getTitleGapClass = (gap?: string): string => {
  switch(gap) {
    case 'mb-2': return 'mb-2';
    case 'mb-4': return 'mb-4';
    case 'mb-6': return 'mb-6';
    case 'mb-8': return 'mb-8';
    case 'mb-10': return 'mb-10';
    case 'mb-12': return 'mb-12';
    case 'mb-16': return 'mb-16';
    default: return 'mb-8';
  }
};

export const PremiumCards: React.FC<PremiumCardsProps> = ({
  title = "Explore Features",
  subtitle,
  titleAlign = 'center',
  titleSize = 'text-3xl',
  titleWeight = 'font-bold',
  subtitleSize = 'text-lg',
  titleGap = 'mb-8',
  items = [],
  variant = 'appstore',
  columns = 3,
  scrollable = true,
  showArrows = true,
  cardWidth = 'medium',
  gap = 24
}) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Card width mapping
  const cardWidthMap = {
    small: 'w-48',
    medium: 'w-64',
    large: 'w-80',
    xl: 'w-96'
  };

  // Handle scroll state
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, [items]);

  // Scroll functions
  const scrollToDirection = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Handle link navigation
  const handleLinkClick = (link: string | undefined, e: React.MouseEvent, linkType?: string) => {
    e.preventDefault();
    if (!link) return;

    // Check link type or auto-detect
    const isExternal = linkType === 'external' || link.startsWith('http://') || link.startsWith('https://');

    if (isExternal) {
      // Ensure proper URL format for external links
      const url = link.startsWith('http') ? link : `https://${link}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Internal app navigation
      const route = link.startsWith('/') ? link : `/${link}`;
      navigate(route);
    }
  };
  const gridCols = scrollable ? '' : {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 md:grid-cols-3';

  const renderNetflixCard = (item: CardItem) => {
    // Get image settings
    const imageAspectRatio = (item as any).imageAspectRatio || 'auto';
    const imageFit = (item as any).imageFit || 'cover';

    // Calculate aspect ratio class
    const aspectClasses = {
      'auto': 'h-48',
      '16:9': 'aspect-video',
      '4:3': 'aspect-4/3',
      '1:1': 'aspect-square',
      '3:2': 'aspect-3/2'
    };

    return (
      <motion.div
        key={item.id}
        className="relative group cursor-pointer w-full h-full"
        variants={netflixCardHover}
        initial="initial"
        whileHover="hover"
      >
        <div className="relative overflow-hidden rounded-lg bg-gray-800">
          {item.image ? (
            <div className={`w-full ${aspectClasses[imageAspectRatio] || 'h-48'} overflow-hidden`}>
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full"
                style={{ objectFit: imageFit as any }}
              />
            </div>
          ) : (
            <div className={`w-full ${aspectClasses[imageAspectRatio] || 'h-48'} bg-gradient-to-br from-gray-700 to-gray-900`} />
          )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className={`absolute bottom-0 left-0 right-0 p-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 ${getTextAlignClass((item as any).textAlign)}`}>
            <h3 className={`text-white ${getFontWeightClass((item as any).titleWeight)} ${getTitleSizeClass((item as any).titleSize)} mb-1`}>
              {item.title}
            </h3>
            {item.subtitle && (
              <p className={`text-gray-300 ${getTitleSizeClass((item as any).descSize)} mb-2`}>{item.subtitle}</p>
            )}
            {(item as any).showCTA !== false && (
              <motion.button
                className="mt-2 px-4 py-2 bg-white text-black rounded-full text-sm font-semibold"
                whileTap={{ scale: 0.95 }}
                onClick={(e) => handleLinkClick(item.link, e, (item as any).linkType)}
              >
                <Play className="w-4 h-4 inline mr-1" />
                {item.ctaText || (item.link?.startsWith('http') ? 'View' : 'Play')}
              </motion.button>
            )}
          </div>
        </div>

        {/* Badge */}
        {item.badge && (
          <span className="absolute top-2 left-2 px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">
            {item.badge}
          </span>
        )}
      </div>
    </motion.div>
    );
  };

  const renderAppStoreCard = (item: CardItem) => {
    // Get image settings
    const imageAspectRatio = (item as any).imageAspectRatio || 'auto';
    const imageFit = (item as any).imageFit || 'cover';

    // Calculate aspect ratio class
    const aspectClasses = {
      'auto': 'h-48',
      '16:9': 'aspect-video',
      '4:3': 'aspect-4/3',
      '1:1': 'aspect-square',
      '3:2': 'aspect-3/2'
    };

    return (
      <motion.div
        key={item.id}
        className="appstore-card group w-full h-full"
        variants={appStoreCardAnimation}
        whileHover={{ y: -8 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className="relative overflow-hidden">
          {item.image ? (
            <div className={`w-full ${aspectClasses[imageAspectRatio] || 'h-48'} overflow-hidden rounded-t-2xl`}>
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full"
                style={{ objectFit: imageFit as any }}
              />
            </div>
          ) : (
            <div
              className={`w-full ${aspectClasses[imageAspectRatio] || 'h-48'} rounded-t-2xl`}
              style={{
                background: `linear-gradient(135deg, ${item.accentColor || '#667eea'} 0%, ${item.accentColor || '#764ba2'} 100%)`
              }}
            />
          )}

        {/* Category Badge */}
        {item.badge && (
          <span
            className="absolute top-4 left-4 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white rounded-full"
            style={{ backgroundColor: item.accentColor || '#007AFF' }}
          >
            {item.badge}
          </span>
        )}
      </div>

      <div className={`p-4 ${getTextAlignClass((item as any).textAlign)}`}>
        <h3 className={`${getTitleSizeClass((item as any).titleSize)} ${getFontWeightClass((item as any).titleWeight)} mb-1 text-gray-900`}>
          {item.title}
        </h3>
        {item.subtitle && (
          <p className={`${getTitleSizeClass((item as any).descSize)} text-gray-600 mb-2`}>{item.subtitle}</p>
        )}
        {item.description && (
          <p className={`${getTitleSizeClass((item as any).descSize)} text-gray-700 mb-3 line-clamp-2`}>{item.description}</p>
        )}

        {/* Rating */}
        {item.rating && (
          <div className="flex items-center gap-0.5 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3 h-3 ${i < (item.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
              />
            ))}
            <span className="text-xs text-gray-600 ml-1">({item.rating}.0)</span>
          </div>
        )}

        {(item as any).showCTA !== false && (
          <motion.button
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            whileTap={{ scale: 0.98 }}
            onClick={(e) => handleLinkClick(item.link, e, (item as any).linkType)}
          >
            {item.ctaText || (item.link?.startsWith('http') ? 'Visit Site' : 'Get Started')}
            <ExternalLink className="w-4 h-4 inline ml-2" />
          </motion.button>
        )}
      </div>
    </motion.div>
    );
  };

  const renderOverlayCard = (item: CardItem) => {
    // Get image settings
    const imageAspectRatio = (item as any).imageAspectRatio || 'auto';
    const imageFit = (item as any).imageFit || 'cover';
    const textPos = item.textPosition || 'bottom';

    // Calculate aspect ratio class
    const aspectClasses = {
      'auto': 'h-80',
      '16:9': 'aspect-video',
      '4:3': 'aspect-4/3',
      '1:1': 'aspect-square',
      '3:2': 'aspect-3/2'
    };

    // Text position classes
    const textPositionClasses = {
      'top': 'justify-start',
      'center': 'justify-center',
      'bottom': 'justify-end'
    };

    return (
      <motion.div
        key={item.id}
        className="relative group cursor-pointer w-full"
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className={`relative ${aspectClasses[imageAspectRatio] || 'h-80'} overflow-hidden rounded-2xl shadow-xl`}>
          {/* Background Image */}
          {item.image ? (
            <img
              src={item.image}
              alt={item.title}
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: imageFit as any }}
            />
          ) : (
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                background: `linear-gradient(135deg, ${item.accentColor || '#667eea'} 0%, ${item.accentColor || '#764ba2'} 100%)`
              }}
            />
          )}

          {/* Gradient Overlay */}
          <div
            className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"
            style={{ opacity: item.overlayIntensity || 0.7 }}
          />

          {/* Content Overlay */}
          <div className={`absolute inset-0 p-6 flex flex-col ${textPositionClasses[textPos] || 'justify-end'} text-white`}>
            <div className={`${getTextAlignClass((item as any).textAlign || (textPos === 'center' ? 'center' : 'left'))}`}>
              {item.badge && (
                <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-wider mb-3">
                  {item.badge}
                </span>
              )}
              <h3 className={`${getTitleSizeClass((item as any).titleSize)} ${getFontWeightClass((item as any).titleWeight)} mb-1 text-shadow-lg`}>
                {item.title}
              </h3>
              {item.subtitle && (
                <p className={`${getTitleSizeClass((item as any).descSize)} text-gray-200 mb-2 text-shadow`}>
                  {item.subtitle}
                </p>
              )}
              {item.description && (
                <p className={`${getTitleSizeClass((item as any).descSize)} text-gray-300 line-clamp-2 text-shadow`}>
                  {item.description}
                </p>
              )}

              {/* CTA Button */}
              {(item as any).showCTA !== false && (
                <motion.button
                  className={`mt-4 px-6 py-3 bg-white text-black rounded-full font-semibold shadow-lg hover:shadow-xl transition-all ${
                    (item as any).textAlign === 'center' ? 'mx-auto' : (item as any).textAlign === 'right' ? 'ml-auto' : ''
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => handleLinkClick(item.link, e, (item as any).linkType)}
                >
                  {item.ctaText || (item.link?.startsWith('http') ? 'Learn More' : 'Get Started')}
                  <ExternalLink className="w-4 h-4 inline ml-2" />
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderGlassCard = (item: CardItem) => {
    // Get image settings
    const imageAspectRatio = (item as any).imageAspectRatio || 'auto';
    const imageFit = (item as any).imageFit || 'cover';

    // Calculate aspect ratio class
    const aspectClasses = {
      'auto': 'h-32',
      '16:9': 'aspect-video',
      '4:3': 'aspect-4/3',
      '1:1': 'aspect-square',
      '3:2': 'aspect-3/2'
    };

    return (
      <motion.div
        key={item.id}
        className="glass-card p-6 group w-full h-full"
        variants={appStoreCardAnimation}
        whileHover={{ scale: 1.02 }}
      >
        <div className="flex flex-col h-full">
          {/* Icon/Image */}
          <div className="mb-4">
            {item.image ? (
              <div className={`w-full ${aspectClasses[imageAspectRatio] || 'h-32'} overflow-hidden rounded-xl`}>
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full"
                  style={{ objectFit: imageFit as any }}
                />
              </div>
            ) : (
              <div
                className={`w-full ${aspectClasses[imageAspectRatio] || 'h-32'} rounded-xl`}
                style={{
                  background: `linear-gradient(135deg, ${item.accentColor || '#667eea'}22 0%, ${item.accentColor || '#764ba2'}22 100%)`
                }}
              />
            )}
          </div>

        {/* Content */}
        <div className={`${getTextAlignClass((item as any).textAlign)}`}>
          <h3 className={`${getTitleSizeClass((item as any).titleSize)} ${getFontWeightClass((item as any).titleWeight)} mb-1 text-gray-900`}>
            {item.title}
          </h3>
          {item.subtitle && (
            <p className={`${getTitleSizeClass((item as any).descSize)} text-gray-600 mb-1`}>{item.subtitle}</p>
          )}
          {item.description && (
            <p className={`${getTitleSizeClass((item as any).descSize)} text-gray-700 mb-3 flex-grow`}>{item.description}</p>
          )}
        </div>

        {/* Action */}
        {(item as any).showCTA !== false && (
          <motion.a
            href={item.link || '#'}
            className={`inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 ${
              (item as any).textAlign === 'center' ? 'mx-auto' : (item as any).textAlign === 'right' ? 'ml-auto' : ''
            }`}
            whileHover={{ x: 5 }}
            onClick={(e) => handleLinkClick(item.link, e, (item as any).linkType)}
          >
            {item.ctaText || (item.link?.startsWith('http') ? 'Learn More' : 'Explore')}
            <ExternalLink className="w-4 h-4 ml-2" />
          </motion.a>
        )}
      </div>
    </motion.div>
    );
  };

  const renderCard = (item: CardItem) => {
    switch (variant) {
      case 'netflix':
        return renderNetflixCard(item);
      case 'glass':
        return renderGlassCard(item);
      case 'overlay':
        return renderOverlayCard(item);
      case 'appstore':
      default:
        return renderAppStoreCard(item);
    }
  };

  return (
    <motion.div
      className="py-12 px-6 max-w-full overflow-hidden"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Section Header */}
      {(title || subtitle) && (
        <div className={`${getTextAlignClass(titleAlign)} ${getTitleGapClass(titleGap)}`}>
          {title && (
            <h2 className={`${getSectionTitleClass(titleSize)} ${getFontWeightClass(titleWeight)} mb-3 heading-premium`}>
              {title}
            </h2>
          )}
          {subtitle && (
            <p className={`${getTitleSizeClass(subtitleSize)} text-gray-600`}>
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Cards Container */}
      {scrollable ? (
        <div className="relative overflow-hidden max-w-full">
          {/* Left Arrow */}
          {showArrows && canScrollLeft && (
            <button
              onClick={() => scrollToDirection('left')}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-gray-800 w-10 h-10 rounded-full shadow-lg backdrop-blur-sm transition-all hover:scale-110 flex items-center justify-center"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Right Arrow */}
          {showArrows && canScrollRight && (
            <button
              onClick={() => scrollToDirection('right')}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-gray-800 w-10 h-10 rounded-full shadow-lg backdrop-blur-sm transition-all hover:scale-110 flex items-center justify-center"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Scrollable Container - Apple TV Style */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto overflow-y-hidden scrollbar-hide scroll-smooth pb-4 max-w-full"
            style={{
              gap: `${gap}px`,
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              width: '100%'
            }}
          >
            {items.map((item) => (
              <div
                key={item.id}
                className={`flex-shrink-0 ${cardWidthMap[cardWidth]}`}
                style={{ scrollSnapAlign: 'start' }}
              >
                {renderCard(item)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <motion.div
          className={`grid ${gridCols} gap-6`}
          variants={staggerContainer}
        >
          {items.map(renderCard)}
        </motion.div>
      )}
    </motion.div>
  );
};